/**
 * =============================================================================
 * MEDIA REVIEW PANEL (ADR-022 §10(C) — PR4, BL-IMG-ADMIN-MODERATION-001)
 * =============================================================================
 *
 * Admin moderation queue for customer-uploaded images. Self-contained
 * big-design Panel (AuditPanel structure): lazy first load, server-side
 * status filter (defaults to PENDING_REVIEW — the review workload), per-status
 * counts, per-image Approve / Reject(reason) / Hide / Show / Remove (PR4b —
 * §10(B) Show = guarded HIDDEN → APPROVED recovery; Remove = confirm-gated
 * soft-delete), bulk actions with per-item result reporting, and offset
 * pagination. Legal-held rows (D-LEGALHOLD) never offer Show/Remove — held
 * evidence stays visible in this listing and is neither deletable nor
 * re-publishable until the hold is released via the runbook.
 *
 * Bulk guardrails (ADR-025 §7.1.4 / blueprint A-M, M6 —
 * BL-ADMIN-WORKBENCH-IA-001D; client-only per DR-2):
 *   - Bulk Approve and bulk Hide are CONFIRM-GATED through an A-0
 *     `AccessibleModal` dialog (M11 hard-pass) stating the action + item
 *     count + public consequence — no one-click mass publish/hide. Bulk
 *     Reject keeps its required-reason `MediaRejectModal` flow untouched.
 *   - "Select all on page" EXCLUDES legal-held rows (with the excluded count
 *     shown); a held row stays individually selectable — the server re-checks
 *     eligibility per item and vetoes regardless (client honesty only).
 *   - The server's already-existing per-item outcomes
 *     (`BulkModerateMediaItemResult[]`) are surfaced after every bulk action:
 *     failures and skipped (not-applied) items render honestly — never a
 *     blanket success.
 *
 * Every thumbnail renders through `AdminImageThumbnail` (authenticated blob
 * via the MEDIA_MODERATE-gated admin byte route) — never a raw `<img src>`.
 * Rejection reasons are a controlled vocabulary; REJECT with
 * SUSPECTED_ILLEGAL is the D-LEGALHOLD illegal-content flow (server-remapped
 * to HIDDEN + preservation hold; surfaced here as "Report illegal").
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FlexItem,
  H2,
  Panel,
  Select,
  Table,
  Text,
} from '@bigcommerce/big-design';
import { RefreshIcon } from '@bigcommerce/big-design-icons';
import { MEDIA_MODERATION_STATUSES } from '@find-my-part/types';
import type {
  AdminMediaListItem,
  BulkModerateMediaItemResult,
  BulkModerateMediaResponse,
  MediaAuthorType,
  MediaModerationAction,
  MediaModerationStatus,
  MediaRejectionReason,
} from '@find-my-part/types';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../hooks/useToast';
import { useMedia } from '../../hooks/useMedia';
import { formatDate } from '../../utils/formatters';
import { AccessibleModal } from '../common/AccessibleModal';
import { useShellNavigation } from '../Shell/ShellNavigationContext';
import type { MediaView } from '../Shell/location';
import { AdminImageThumbnail } from './AdminImageThumbnail';
import { MediaStatusBadge } from './MediaStatusBadge';
import { MediaRejectModal, MEDIA_REJECTION_REASON_LABELS } from './MediaRejectModal';
import { EmptyState } from '../common/EmptyState';
import { PageHeader } from '../common/PageHeader';
import { Toolbar } from '../common/Toolbar';

const STATUS_LABELS: Record<MediaModerationStatus, string> = {
  PENDING_REVIEW: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  HIDDEN: 'Hidden',
};

const STATUS_OPTIONS = [
  { value: '', content: 'All statuses' },
  ...MEDIA_MODERATION_STATUSES.map((s) => ({ value: s, content: STATUS_LABELS[s] })),
];

const UPLOADER_LABELS: Record<MediaAuthorType, string> = {
  GUEST: 'Guest',
  CUSTOMER: 'Customer',
  ADMIN: 'Staff',
};

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * The media row's parent-question reference (ADR-025 B-2 — §3.1.6 / M8).
 *
 * Inside the shell (a `ShellNavigationContext` is present) it is a REAL
 * control: a button that dispatches `{ section: 'questions', questionId }` —
 * filtered-section navigation, zero URL writes. The Questions section resolves
 * the id against its currently loaded rows and opens the existing detail
 * modal (fail-safe when the question is on another page/filter).
 *
 * WITHOUT a shell (the flag-OFF legacy layout — the §3.5 rollback surface —
 * and bare-panel tests) it renders EXACTLY the pre-B-2 plain text, so the
 * legacy surface's behaviour stays byte-identical (M9, pinned).
 */
function MediaQuestionRef({
  questionId,
  attachmentId,
  testId,
}: {
  questionId: string;
  attachmentId: string;
  testId: string;
}): React.ReactElement {
  const shellNavigation = useShellNavigation();

  if (!shellNavigation) {
    return (
      <Text color="secondary">
        Question: {questionId}
      </Text>
    );
  }

  return (
    <Button
      variant="subtle"
      onClick={() => shellNavigation.navigate({ section: 'questions', questionId })}
      // The accessible name states the ACTION and the target id (the visible
      // text alone reads as a label, not an affordance).
      aria-label={`Open question ${questionId}`}
      data-testid={`${testId}-question-link-${attachmentId}`}
    >
      Question: {questionId}
    </Button>
  );
}

/** Target of the reject modal: one attachment, or the bulk selection. */
type RejectTarget =
  | { mode: 'single'; attachmentId: string; lockToSuspectedIllegal: boolean }
  | { mode: 'bulk' };

/**
 * Pending confirm-gated bulk action (A-M / M6). The TARGET ID LIST is
 * snapshotted at open and is exactly what confirming submits, so the dialog
 * header's count is structurally honest — it can never diverge from the
 * request, whatever happens to the live selection.
 */
type BulkConfirmTarget = { action: 'APPROVE' | 'HIDE'; attachmentIds: string[] };

/** Lower-case verbs for the per-item outcome summary line. */
const BULK_ACTION_VERBS: Record<MediaModerationAction, string> = {
  APPROVE: 'approve',
  REJECT: 'reject',
  HIDE: 'hide',
  SHOW: 'show',
};

export interface MediaReviewPanelProps {
  /** Optional test-id prefix (default "media-review"). */
  testId?: string;
  /** B-3 semantic filtered media destination. */
  view?: MediaView;
  /** Repeatable explicit destination intent; bare rail re-entry leaves it unchanged. */
  viewIntentNonce?: number;
  /**
   * Shell activation state. Hidden keep-alive panels retain queue/filter state
   * while suspending authenticated thumbnail and preview resources.
   */
  isActive?: boolean;
}

export function MediaReviewPanel({
  testId = 'media-review',
  view,
  viewIntentNonce,
  isActive = true,
}: MediaReviewPanelProps): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();
  const { showSuccess, showError } = useToast();
  const {
    media,
    pagination,
    counts,
    isLoading,
    isMutating,
    error,
    hasLoaded,
    applyFilters,
    goToPage,
    refresh,
    moderate,
    moderateBulk,
    remove,
  } = useMedia(token ?? '');

  // Server-side status filter. Defaults to the review workload.
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_REVIEW');
  // Manual row selection (bulk actions).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
  // ext-R1-F1 (external review): stable A-0 focus fallback. A confirmed bulk
  // action (or a confirmed remove) refreshes the list; the selection-clear
  // effect then unmounts the bulk bar — and with it the dialog's trigger — so
  // A-0's primary restore target is detached. Falling back here keeps focus
  // off <body> (ADR-025 M11).
  const refreshBtnRef = useRef<HTMLButtonElement>(null);
  // ext-R1-C3: big-design's Table re-creates cell DOM on ANY panel re-render
  // (probe-verified), so the exact node a moderator focused is already dead
  // by the time a row-triggered dialog closes. These record WHICH row control
  // opened the dialog; restoreFocusTo re-queries the CURRENT equivalent node
  // at restore time (Cancel/Esc → same row control; row gone after a
  // confirmed remove → A-0 falls back to Refresh).
  const lastRemoveTriggerIdRef = useRef<string | null>(null);
  const lastPreviewTriggerIdRef = useRef<string | null>(null);
  // B-6 (design-v3 §D.4): which control opened the reject/report dialog —
  // row Reject…, row Report illegal…, or the bulk-bar Reject… — so Cancel/Esc
  // restores to the CURRENT equivalent node (Table recreates cell DOM on any
  // panel render; a captured element reference would be stale).
  const lastRejectTriggerIdRef = useRef<string | null>(null);
  const queryByTestId = useCallback(
    (id: string): HTMLElement | null =>
      document.querySelector<HTMLElement>(`[data-testid="${testId}-${id}"]`),
    [testId]
  );
  // Confirm-gated bulk Approve/Hide target (A-M / M6 — never one-click).
  const [bulkConfirm, setBulkConfirm] = useState<BulkConfirmTarget | null>(null);
  // Per-item outcomes of the LAST bulk action: kept visible (the toast is
  // transient) whenever any item failed or was skipped, so a partial result
  // is never presented as a blanket success. Cleared on dismiss, replaced by
  // the next bulk action.
  const [bulkItemOutcomes, setBulkItemOutcomes] = useState<{
    action: MediaModerationAction;
    results: BulkModerateMediaItemResult[];
  } | null>(null);
  // Confirm-gated remove target (PR4b — the delete is a soft-delete, but a
  // removed APPROVED question image changes the public page, so it is never
  // a single-click action here).
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  // Lightbox preview. PR2c: the clicked AdminImageThumbnail fetches the
  // FULL-SIZE bytes on demand and passes THAT object URL here (the grid only
  // ever downloads 256px thumbs; moderation review must inspect originals).
  // The URL is still BORROWED from the component's owned object URL — the
  // same lifecycle the external round verified non-manifesting (OPUS4.8-F5):
  // a revoke only invalidates FUTURE resolutions, and the preview <img>
  // resolves the blob synchronously on the same commit as setPreviewUrl.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // A preview is a borrowed full-size lease owned by its thumbnail. Closing
  // it at the section boundary prevents a hidden modal from retaining or
  // referring to a resource that deactivation intentionally releases.
  useEffect(() => {
    if (!isActive) setPreviewUrl(null);
  }, [isActive]);

  // A reload (filter change, refresh, page change) invalidates the selection —
  // rows may have left the current view.
  useEffect(() => {
    setSelected(new Set());
  }, [media]);

  const loadWithStatus = useCallback(
    (status: string): Promise<void> =>
      applyFilters(status ? { status: status as MediaModerationStatus } : {}),
    [applyFilters]
  );

  const handleFirstLoad = useCallback((): void => {
    void loadWithStatus(statusFilter);
  }, [loadWithStatus, statusFilter]);

  useEffect(() => {
    if (view !== 'pendingReview') return;
    setStatusFilter('PENDING_REVIEW');
    void loadWithStatus('PENDING_REVIEW');
  }, [loadWithStatus, view, viewIntentNonce]);

  const handleStatusChange = useCallback(
    (value: string): void => {
      setStatusFilter(value ?? '');
      void loadWithStatus(value ?? '');
    },
    [loadWithStatus]
  );

  const reportOutcome = useCallback(
    (outcome: { success: boolean; message: string }): void => {
      if (outcome.success) {
        showSuccess(outcome.message);
      } else {
        showError(outcome.message);
      }
    },
    [showSuccess, showError]
  );

  const handleSingleAction = useCallback(
    async (attachmentId: string, action: MediaModerationAction): Promise<void> => {
      if (isMutating) return;
      reportOutcome(await moderate(attachmentId, action));
    },
    [isMutating, moderate, reportOutcome]
  );

  /**
   * Toast the bulk summary AND retain the per-item outcomes (A-M / M6): the
   * server reports `BulkModerateMediaItemResult[]` per item; any failure or
   * skipped (not-applied) item stays visible in the results box below the
   * bulk bar — the toast alone is transient and its summary counts a no-op
   * as "processed".
   */
  const surfaceBulkOutcome = useCallback(
    (
      action: MediaModerationAction,
      outcome: { success: boolean; message: string; data?: BulkModerateMediaResponse }
    ): void => {
      reportOutcome(outcome);
      const results = outcome.data?.results ?? [];
      const hasFailuresOrSkips = results.some((r) => !r.success || r.applied === false);
      setBulkItemOutcomes(hasFailuresOrSkips ? { action, results } : null);
    },
    [reportOutcome]
  );

  const handleRejectConfirm = useCallback(
    async (reason: MediaRejectionReason): Promise<void> => {
      if (!rejectTarget) return;
      if (rejectTarget.mode === 'single') {
        reportOutcome(await moderate(rejectTarget.attachmentId, 'REJECT', reason));
      } else {
        surfaceBulkOutcome('REJECT', await moderateBulk([...selected], 'REJECT', reason));
      }
      setRejectTarget(null);
    },
    [rejectTarget, selected, moderate, moderateBulk, reportOutcome, surfaceBulkOutcome]
  );

  const handleRemoveConfirm = useCallback(async (): Promise<void> => {
    if (!removeTarget || isMutating) return;
    reportOutcome(await remove(removeTarget));
    setRemoveTarget(null);
  }, [removeTarget, isMutating, remove, reportOutcome]);

  const handleBulkAction = useCallback(
    (action: MediaModerationAction): void => {
      if (isMutating || selected.size === 0) return;
      if (action === 'REJECT') {
        lastRejectTriggerIdRef.current = 'bulk-reject';
        setRejectTarget({ mode: 'bulk' });
        return;
      }
      // A-M confirm gate (ADR-025 §7.1.4 / M6): bulk Approve and bulk Hide
      // never fire on one click — a confirm dialog states the action, the
      // item count, and the public consequence first. (SHOW has no bulk
      // control; the exhaustive check keeps a future one from bypassing
      // the gate silently.)
      if (action === 'APPROVE' || action === 'HIDE') {
        setBulkConfirm({ action, attachmentIds: [...selected] });
      }
    },
    [isMutating, selected]
  );

  const handleBulkConfirm = useCallback(async (): Promise<void> => {
    if (!bulkConfirm || isMutating) return;
    // Submit the SNAPSHOTTED ids the dialog described — never the live
    // selection, which a list reload could have pruned mid-dialog.
    const outcome = await moderateBulk(bulkConfirm.attachmentIds, bulkConfirm.action);
    surfaceBulkOutcome(bulkConfirm.action, outcome);
    setBulkConfirm(null);
  }, [bulkConfirm, isMutating, moderateBulk, surfaceBulkOutcome]);

  const toggleSelected = useCallback((attachmentId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(attachmentId)) {
        next.delete(attachmentId);
      } else {
        next.add(attachmentId);
      }
      return next;
    });
  }, []);

  // A-M / M6: "select all" NEVER sweeps in legal-held rows — held evidence
  // must not enter a mass publish/hide by default. A held row can still be
  // selected individually (the server re-checks per item and vetoes
  // regardless); the excluded count is shown beside the checkbox.
  const selectableMedia = useMemo(() => media.filter((m) => !m.legalHold), [media]);
  const heldOnPageCount = media.length - selectableMedia.length;
  const allOnPageSelected =
    selectableMedia.length > 0 &&
    selectableMedia.every((m) => selected.has(m.attachmentId));
  const toggleSelectAll = useCallback((): void => {
    setSelected(
      allOnPageSelected ? new Set() : new Set(selectableMedia.map((m) => m.attachmentId))
    );
  }, [allOnPageSelected, selectableMedia]);

  // BigDesign treats a column render function as the React component type for
  // each cell. Keeping this function stable preserves the resolved thumbnail
  // DOM across unrelated session/shell renders; changing `isActive` remains an
  // intentional boundary that remounts the cell and releases/reacquires its
  // resource lease.
  const renderImageCell = useCallback(
    (item: AdminMediaListItem): React.ReactElement => (
      <AdminImageThumbnail
        src={item.url}
        alt={item.originalFilename ?? item.attachmentId}
        token={token ?? ''}
        size={64}
        active={isActive}
        onClick={(blobUrl) => {
          lastPreviewTriggerIdRef.current = item.attachmentId;
          setPreviewUrl(blobUrl);
        }}
        testId={`${testId}-thumb-${item.attachmentId}`}
      />
    ),
    [isActive, testId, token]
  );

  if (!isAuthenticated) return null;

  // Per-item outcome partitions for the results box (A-M / M6). `applied` is
  // optional on the wire; a successful item is a skip exactly when the server
  // said `applied: false` (idempotent no-op or a lost CAS race).
  const bulkFailures = bulkItemOutcomes?.results.filter((r) => !r.success) ?? [];
  const bulkSkips =
    bulkItemOutcomes?.results.filter((r) => r.success && r.applied === false) ?? [];
  const bulkAppliedCount = bulkItemOutcomes
    ? bulkItemOutcomes.results.length - bulkFailures.length - bulkSkips.length
    : 0;

  // Dialog header states the action + count honestly (M6) — derived from the
  // snapshotted ids the confirm will actually submit. The fallback string
  // only satisfies the A-0 unnamed-dialog invariant while closed — it is
  // never rendered.
  const bulkConfirmCount = bulkConfirm?.attachmentIds.length ?? 0;
  const bulkConfirmHeader = bulkConfirm
    ? `${bulkConfirm.action === 'APPROVE' ? 'Approve' : 'Hide'} ${bulkConfirmCount} ${
        bulkConfirmCount === 1 ? 'image' : 'images'
      }`
    : 'Confirm bulk action';

  return (
    <Panel data-testid={testId}>
      {/* Header */}
      <PageHeader
        paddingBottom="none"
        heading={<H2 marginBottom="none">Media review</H2>}
        action={
          hasLoaded ? (
            <Button
              ref={refreshBtnRef}
              variant="subtle"
              onClick={() => void refresh()}
              disabled={isLoading || isMutating}
              iconLeft={<RefreshIcon />}
              data-testid={`${testId}-refresh-btn`}
            >
              Refresh
            </Button>
          ) : null
        }
      >
        <Text color="secondary">
          Customer-uploaded images stay hidden until approved. Rejection reasons are
          recorded in the audit trail and never shown to the poster.
        </Text>
      </PageHeader>

      {/* Lazy first load */}
      {!hasLoaded && (
        <Box padding="medium">
          <Button
            onClick={handleFirstLoad}
            isLoading={isLoading}
            data-testid={`${testId}-load-btn`}
          >
            Load review queue
          </Button>
        </Box>
      )}

      {hasLoaded && (
        <>
          {/* Per-status counts */}
          {counts && (
            <Box padding="medium" paddingBottom="none">
              <Flex flexGap="2rem" flexWrap="wrap">
                {MEDIA_MODERATION_STATUSES.map((s) => (
                  <FlexItem key={s}>
                    <Text
                      bold
                      data-testid={`${testId}-count-${s.toLowerCase()}`}
                    >
                      {counts[s]} {STATUS_LABELS[s]}
                    </Text>
                  </FlexItem>
                ))}
              </Flex>
            </Box>
          )}

          {/* Filters */}
          <Box padding="medium" paddingBottom="none">
            <Toolbar preset="filters">
              <FlexItem style={{ width: '220px' }}>
                <Select
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onOptionChange={handleStatusChange}
                  data-testid={`${testId}-status-filter`}
                />
              </FlexItem>
              {media.length > 0 && (
                <FlexItem paddingBottom="xSmall">
                  <Checkbox
                    label="Select all on page"
                    checked={allOnPageSelected}
                    disabled={selectableMedia.length === 0}
                    onChange={toggleSelectAll}
                    data-testid={`${testId}-select-all`}
                  />
                </FlexItem>
              )}
              {heldOnPageCount > 0 && (
                <FlexItem paddingBottom="xSmall">
                  <Text
                    color="secondary"
                    data-testid={`${testId}-select-all-excluded`}
                  >
                    {heldOnPageCount} legal-held{' '}
                    {heldOnPageCount === 1 ? 'item' : 'items'} excluded from
                    select-all
                  </Text>
                </FlexItem>
              )}
            </Toolbar>
          </Box>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <Box padding="medium" paddingBottom="none">
              <Toolbar preset="actions">
                <FlexItem>
                  <Text bold data-testid={`${testId}-selected-count`}>
                    {selected.size} selected
                  </Text>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="primary"
                    disabled={isMutating}
                    onClick={() => handleBulkAction('APPROVE')}
                    data-testid={`${testId}-bulk-approve`}
                  >
                    Approve
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="secondary"
                    actionType="destructive"
                    disabled={isMutating}
                    onClick={() => handleBulkAction('REJECT')}
                    data-testid={`${testId}-bulk-reject`}
                  >
                    Reject…
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="secondary"
                    disabled={isMutating}
                    onClick={() => handleBulkAction('HIDE')}
                    data-testid={`${testId}-bulk-hide`}
                  >
                    Hide
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="subtle"
                    disabled={isMutating}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear selection
                  </Button>
                </FlexItem>
              </Toolbar>
            </Box>
          )}

          {/* Per-item bulk outcomes (A-M / M6): shown whenever the last bulk
              action had failures or skipped items — the transient toast
              summary alone would read as a blanket success. Persists until
              dismissed or the next bulk action. */}
          {bulkItemOutcomes && (
            <Box padding="medium" paddingBottom="none">
              <Box
                border="box"
                borderRadius="normal"
                padding="small"
                data-testid={`${testId}-bulk-results`}
              >
                {/* No aria-live here: a region mounted already-populated is
                    not reliably announced; SR users get the summary via the
                    toast (big-design alerts). SR announcement of per-item
                    outcomes is tracked as
                    BL-MEDIA-BULK-RESULTS-LIVE-REGION-001 (the counts
                    live-region pattern lands with A-W/B-3, ext-B-missing-3;
                    this box adopts it there). */}
                <Flex justifyContent="space-between" alignItems="center">
                  <FlexItem>
                    <Text bold marginBottom="none">
                      Bulk {BULK_ACTION_VERBS[bulkItemOutcomes.action]}:{' '}
                      {bulkAppliedCount} of {bulkItemOutcomes.results.length} applied
                      {bulkFailures.length > 0 ? `, ${bulkFailures.length} failed` : ''}
                      {bulkSkips.length > 0 ? `, ${bulkSkips.length} skipped` : ''}
                    </Text>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="subtle"
                      onClick={() => setBulkItemOutcomes(null)}
                      data-testid={`${testId}-bulk-results-dismiss`}
                    >
                      Dismiss
                    </Button>
                  </FlexItem>
                </Flex>
                {bulkFailures.map((r) => (
                  <Text
                    key={r.attachmentId}
                    color="danger"
                    marginBottom="none"
                    data-testid={`${testId}-bulk-result-${r.attachmentId}`}
                  >
                    {r.attachmentId}: {r.error?.message ?? 'Failed'}
                  </Text>
                ))}
                {bulkSkips.map((r) => (
                  <Text
                    key={r.attachmentId}
                    color="secondary"
                    marginBottom="none"
                    data-testid={`${testId}-bulk-result-${r.attachmentId}`}
                  >
                    {r.attachmentId}: not applied — already in this state or
                    changed by another moderator
                  </Text>
                ))}
              </Box>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Box padding="medium">
              <Text color="danger" data-testid={`${testId}-error`}>
                {error}
              </Text>
            </Box>
          )}

          {/* Empty state */}
          {!isLoading && !error && media.length === 0 && (
            <EmptyState
              variant="plain"
              testId={`${testId}-empty`}
              body={
                statusFilter === 'PENDING_REVIEW'
                  ? 'No images awaiting review.'
                  : 'No images found.'
              }
            />
          )}

          {/* Queue table */}
          {media.length > 0 && (
            <Box padding="medium" paddingTop="small">
              {/* ADR-025 A-H (M13): the table scrolls INSIDE this container — the
                  page body must never scroll sideways at supported widths. */}
              <Box data-testid="media-review-table-scroll" style={{ overflowX: 'auto' }}>
                <Table
                  itemName="images"
                  // Row identity MUST be the attachment, not the row index:
                  // without it, pagination/filter/refresh reconciles recycled
                  // rows in place and per-row component state (e.g. the
                  // thumbnail's cached full-size blob) would belong to the
                  // previous attachment (backbone review fold).
                  keyField="attachmentId"
                  columns={[
                    {
                      header: 'Select',
                      hideHeader: true,
                      hash: 'select',
                      width: 40,
                      render: ({ attachmentId }: AdminMediaListItem) => (
                        <Checkbox
                          label=""
                          hiddenLabel
                          aria-label={`Select image ${attachmentId}`}
                          checked={selected.has(attachmentId)}
                          onChange={() => toggleSelected(attachmentId)}
                          data-testid={`${testId}-select-${attachmentId}`}
                        />
                      ),
                    },
                    {
                      header: 'Image',
                      hash: 'image',
                      render: renderImageCell,
                    },
                    {
                      header: 'Details',
                      hash: 'details',
                      render: (item: AdminMediaListItem) => (
                        <Flex flexDirection="column" flexGap="2px">
                          <Text bold>
                            {item.originalFilename ?? item.attachmentId}
                          </Text>
                          <Text color="secondary">
                            {formatFileSize(item.fileSize)}
                            {item.width && item.height
                              ? ` · ${item.width}×${item.height}`
                              : ''}
                          </Text>
                          {/* B-2: inside the shell this is a navigation
                              affordance to the parent question; without one
                              (legacy layout) it stays the plain text label.
                              Only question-anchored rows (non-null id) get it. */}
                          {item.questionId && (
                            <MediaQuestionRef
                              questionId={item.questionId}
                              attachmentId={item.attachmentId}
                              testId={testId}
                            />
                          )}
                          {/* Reply-anchored context (ADR-022 §14 D5): a reply image
                              is denormalized onto its question, so the "Question"
                              line alone is ambiguous — label it a reply image and
                              name the reply. No routing (ADR-025 owns nav). */}
                          {item.replyId && (
                            <Text
                              color="secondary"
                              data-testid={`${testId}-reply-context-${item.attachmentId}`}
                            >
                              Reply image · Reply: {item.replyId}
                            </Text>
                          )}
                          {item.rejectionReason && (
                            <Text color="secondary">
                              Reason: {MEDIA_REJECTION_REASON_LABELS[item.rejectionReason]}
                            </Text>
                          )}
                        </Flex>
                      ),
                    },
                    {
                      header: 'Uploader',
                      hash: 'uploader',
                      render: ({ uploadedByType }: AdminMediaListItem) => (
                        <Text>
                          {UPLOADER_LABELS[uploadedByType]}
                        </Text>
                      ),
                    },
                    {
                      header: 'Submitted',
                      hash: 'submitted',
                      render: ({ createdAt }: AdminMediaListItem) => (
                        <Text>
                          {formatDate(createdAt)}
                        </Text>
                      ),
                    },
                    {
                      header: 'Status',
                      hash: 'status',
                      render: (item: AdminMediaListItem) => (
                        <Flex flexDirection="column" flexGap="4px" alignItems="flex-start">
                          <MediaStatusBadge
                            status={item.moderationStatus}
                            data-testid={`${testId}-status-${item.attachmentId}`}
                          />
                          {item.legalHold && (
                            <Badge
                              variant="danger"
                              label="Legal hold"
                              aria-label="Preserved under legal hold"
                              data-testid={`${testId}-hold-${item.attachmentId}`}
                            />
                          )}
                        </Flex>
                      ),
                    },
                    {
                      header: 'Actions',
                      hash: 'actions',
                      render: (item: AdminMediaListItem) => (
                        <Toolbar preset="cluster">
                          {item.moderationStatus === 'PENDING_REVIEW' && (
                            <>
                              <FlexItem>
                                <Button
                                  variant="primary"
                                  disabled={isMutating}
                                  onClick={() =>
                                    void handleSingleAction(item.attachmentId, 'APPROVE')
                                  }
                                  data-testid={`${testId}-approve-${item.attachmentId}`}
                                >
                                  Approve
                                </Button>
                              </FlexItem>
                              <FlexItem>
                                <Button
                                  variant="secondary"
                                  actionType="destructive"
                                  disabled={isMutating}
                                  onClick={() => {
                                    lastRejectTriggerIdRef.current = `reject-${item.attachmentId}`;
                                    setRejectTarget({
                                      mode: 'single',
                                      attachmentId: item.attachmentId,
                                      lockToSuspectedIllegal: false,
                                    });
                                  }}
                                  data-testid={`${testId}-reject-${item.attachmentId}`}
                                >
                                  Reject…
                                </Button>
                              </FlexItem>
                            </>
                          )}
                          {item.moderationStatus === 'APPROVED' && (
                            <FlexItem>
                              <Button
                                variant="secondary"
                                disabled={isMutating}
                                onClick={() =>
                                  void handleSingleAction(item.attachmentId, 'HIDE')
                                }
                                data-testid={`${testId}-hide-${item.attachmentId}`}
                              >
                                Hide
                              </Button>
                            </FlexItem>
                          )}
                          {/* SHOW (PR4b — §10(B) guarded HIDDEN → APPROVED recovery).
                              Not offered on a held row: re-publishing held evidence
                              is always vetoed (D-LEGALHOLD); a concurrent-hold race
                              still 409s honestly and toasts the hold message. */}
                          {item.moderationStatus === 'HIDDEN' && !item.legalHold && (
                            <FlexItem>
                              <Button
                                variant="subtle"
                                disabled={isMutating}
                                onClick={() =>
                                  void handleSingleAction(item.attachmentId, 'SHOW')
                                }
                                data-testid={`${testId}-show-${item.attachmentId}`}
                              >
                                Show
                              </Button>
                            </FlexItem>
                          )}
                          {/* Remove (PR4b — §10(B) confirm-gated soft-delete). A held
                              row keeps a DISABLED control with the hold explanation:
                              held evidence must stay visible in this listing for the
                              DRC workflow and is never deletable (D-LEGALHOLD). */}
                          <FlexItem>
                            <Button
                              variant="subtle"
                              disabled={isMutating || item.legalHold}
                              title={
                                item.legalHold
                                  ? 'Legal hold: this image is preserved and cannot be removed.'
                                  : undefined
                              }
                              aria-label={
                                item.legalHold
                                  ? `Remove image ${item.attachmentId} (blocked by legal hold)`
                                  : `Remove image ${item.attachmentId}`
                              }
                              onClick={() => {
                                lastRemoveTriggerIdRef.current = item.attachmentId;
                                setRemoveTarget(item.attachmentId);
                              }}
                              data-testid={`${testId}-remove-${item.attachmentId}`}
                            >
                              Remove…
                            </Button>
                          </FlexItem>
                          {!item.legalHold && (
                            <FlexItem>
                              <Button
                                variant="subtle"
                                actionType="destructive"
                                disabled={isMutating}
                                onClick={() => {
                                  lastRejectTriggerIdRef.current = `illegal-${item.attachmentId}`;
                                  setRejectTarget({
                                    mode: 'single',
                                    attachmentId: item.attachmentId,
                                    lockToSuspectedIllegal: true,
                                  });
                                }}
                                data-testid={`${testId}-illegal-${item.attachmentId}`}
                              >
                                Report illegal…
                              </Button>
                            </FlexItem>
                          )}
                        </Toolbar>
                      ),
                    },
                  ]}
                  items={media}
                  data-testid={`${testId}-table`}
                />
              </Box>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <Box paddingTop="medium">
                  <Toolbar justifyContent="flex-end" alignItems="center" flexGap="1rem">
                    <FlexItem>
                      <Text color="secondary">
                        Page {pagination.page} of {pagination.totalPages} ·{' '}
                        {pagination.total} images
                      </Text>
                    </FlexItem>
                    <FlexItem>
                      <Button
                        variant="secondary"
                        disabled={isLoading || isMutating || pagination.page <= 1}
                        onClick={() => void goToPage(pagination.page - 1)}
                        data-testid={`${testId}-prev-page`}
                      >
                        Previous
                      </Button>
                    </FlexItem>
                    <FlexItem>
                      <Button
                        variant="secondary"
                        disabled={isLoading || isMutating || pagination.page >= pagination.totalPages}
                        onClick={() => void goToPage(pagination.page + 1)}
                        data-testid={`${testId}-next-page`}
                      >
                        Next
                      </Button>
                    </FlexItem>
                  </Toolbar>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {/* Reject reason modal (single + bulk; locked mode = report illegal) */}
      <MediaRejectModal
        isOpen={rejectTarget !== null}
        targetCount={rejectTarget?.mode === 'bulk' ? selected.size : 1}
        lockToSuspectedIllegal={
          rejectTarget?.mode === 'single' && rejectTarget.lockToSuspectedIllegal
        }
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectTarget(null)}
        isSubmitting={isMutating}
        restoreFocusTo={() =>
          lastRejectTriggerIdRef.current && isActive
            ? queryByTestId(lastRejectTriggerIdRef.current)
            : null
        }
        fallbackFocusTo={() => (isActive ? refreshBtnRef.current : null)}
        testId={`${testId}-reject-modal`}
      />

      {/* Bulk Approve/Hide confirm (A-M — ADR-025 §7.1.4 / M6): an A-0
          `AccessibleModal` consumer under the M11 hard-pass. Kept MOUNTED
          (isOpen-toggled) so A-0 can capture the trigger for focus
          restoration; fallbackFocusTo covers the CONFIRMED path, where the
          refresh-driven selection clear unmounts the bulk bar (and trigger)
          before restore runs (ext-R1-F1). Bulk Reject stays on
          MediaRejectModal above. */}
      <AccessibleModal
        isOpen={bulkConfirm !== null}
        onClose={() => setBulkConfirm(null)}
        closeOnEscKey={!isMutating}
        closeOnClickOutside={!isMutating}
        header={bulkConfirmHeader}
        fallbackFocusTo={refreshBtnRef}
        actions={[
          {
            text: 'Cancel',
            variant: 'secondary' as const,
            onClick: () => setBulkConfirm(null),
            disabled: isMutating,
          },
          {
            text: bulkConfirm?.action === 'HIDE' ? 'Hide' : 'Approve',
            variant: 'primary' as const,
            ...(bulkConfirm?.action === 'HIDE'
              ? { actionType: 'destructive' as const }
              : {}),
            onClick: () => {
              void handleBulkConfirm();
            },
            disabled: isMutating,
            isLoading: isMutating,
          },
        ]}
        data-testid={`${testId}-bulk-confirm-modal`}
      >
        <Text>
          {bulkConfirm?.action === 'HIDE'
            ? 'Hidden images are removed from public view on the storefront. Items the server cannot hide (for example under a legal hold) are reported per item.'
            : 'Approved images become publicly visible on the storefront. Items the server cannot approve (for example under a legal hold) are reported per item.'}
        </Text>
      </AccessibleModal>

      {/* Remove confirm (PR4b — §10(B) soft-delete). ext-R1-C3 (M11
          file-scope): this file changed in A-M, so this dialog enters the
          hard-pass — retrofitted to A-0 and kept MOUNTED (isOpen-toggled;
          the old `{removeTarget && <Modal isOpen>}` mount-as-open pattern
          unmounted it in the same commit as close, so A-0's restore could
          never fire). A confirmed remove refreshes the list and detaches the
          row's Remove trigger → fallback to Refresh. */}
      <AccessibleModal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        closeOnEscKey
        closeOnClickOutside={!isMutating}
        header="Remove image"
        restoreFocusTo={() =>
          lastRemoveTriggerIdRef.current
            ? queryByTestId(`remove-${lastRemoveTriggerIdRef.current}`)
            : null
        }
        fallbackFocusTo={refreshBtnRef}
        actions={[
          {
            text: 'Cancel',
            variant: 'secondary' as const,
            onClick: () => setRemoveTarget(null),
            disabled: isMutating,
          },
          {
            text: 'Remove',
            variant: 'primary' as const,
            actionType: 'destructive' as const,
            onClick: () => {
              void handleRemoveConfirm();
            },
            disabled: isMutating,
            isLoading: isMutating,
          },
        ]}
        data-testid={`${testId}-remove-modal`}
      >
        <Text>
          The image is removed from every surface, including the public page if it
          was approved. This cannot be undone from this screen.
        </Text>
      </AccessibleModal>

      {/* Lightbox preview — same ext-R1-C3 retrofit + keep-mounted pattern.
          Closing restores focus to the thumbnail trigger (the list is intact
          on this path); fallback covers a refresh racing the preview. */}
      <AccessibleModal
        isOpen={previewUrl !== null}
        onClose={() => setPreviewUrl(null)}
        closeOnEscKey
        closeOnClickOutside
        header="Image preview"
        restoreFocusTo={() =>
          isActive && lastPreviewTriggerIdRef.current
            ? queryByTestId(`thumb-${lastPreviewTriggerIdRef.current}`)
            : null
        }
        fallbackFocusTo={() => (isActive ? refreshBtnRef.current : null)}
        data-testid={`${testId}-preview-modal`}
      >
        {previewUrl && (
          <Box style={{ textAlign: 'center' }}>
            <img
              src={previewUrl}
              alt="Full-size preview"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        )}
      </AccessibleModal>
    </Panel>
  );
}
